// 简历数据模型
export interface Education {
  schoolName: string;
  major: string;
  startDate: string; // 格式：YYYY-MM
  endDate: string; // 格式：YYYY-MM
  awards: string; // 在校获奖
  schoolLevel: number[]; // 1-普通 2-双一流 3-211 4-985
}

export interface WorkExperience {
  companyName: string;
  position: string;
  workContent: string;
  workAwards: string;
  startDate: string; // 格式：YYYY-MM
  endDate: string; // 格式：YYYY-MM
  skills: string; // 技能描述
}

export interface Paper {
  journalName: string;
  paperTitle: string;
  isFirstAuthor: boolean;
  url: string;
}

export interface Certificate {
  name: string;
  imageUrl: string;
}

export interface Resume {
  id: string;
  name: string;
  birthDate: string; // 格式：YYYY-MM-DD
  householdCity: string;
  wechatId: string;
  email: string;
  phone: string;
  firstWorkDate: string; // 格式：YYYY-MM
  expectedSalaryStart: number; // 期望薪资范围开始（K）
  expectedSalaryEnd: number; // 期望薪资范围结束（K）
  remoteWorkPhotoUrl: string; // 远程办公环境照片
  personalAdvantage: string; // 个人优势
  education: number; // 学历：1-高中 2-中专 3-大专 4-三本学士 5-二本学士 6-一本学士 7-硕士研究生 8-博士生
  gender: number; // 性别：0-女 1-男
  educationHistory: Education[]; // 学习经历
  workHistory: WorkExperience[]; // 工作经历
  papers: Paper[]; // 发表论文
  certificates: Certificate[]; // 资格证书
  bestSchoolName: string; // 最佳学校的名称
  bestCompanyName: string; // 最佳公司的名称
  bestSchoolType: number; // 最佳学校的类型：1-普通大学 2-双一流 3-211/985 4-复北清交
  bestCompanyType: number; // 最佳公司的类型：1-小微企业 2-中型企业 3-二线互联网企业 4-一线互联网 5-BBAT（百度字节阿里腾讯） 
  servedMonthCount: number; // 服务过的月数
  isServing: boolean; // 当前是否正在服务中
  createdAt: string;
  updatedAt: string;
}

// 筛选条件接口
export interface ResumeFilter {
  keyword?: string; // 搜索关键词
  education?: number; // 学历筛选
  bestSchoolType?: number; // 最佳学校类型筛选
  bestCompanyType?: number; // 最佳公司类型筛选
  expectedSalary?: number; // 参考薪资
  minWorkYears?: number; // 最小工作年限
  maxWorkYears?: number; // 最大工作年限
  minAge?: number; // 最小年龄
  maxAge?: number; // 最大年龄
  sortBy?: 'createdAt' | 'servedMonthCount' | 'age'; // 排序字段
  sortOrder?: 'asc' | 'desc'; // 排序方向
  sellerIdOrPhone?: string; // 卖家ID或手机号
  sellerNicknameOrKeyword?: string; // 卖家昵称或简历内容关键词
  isServing?: boolean; // 是否在服务中
}

// 学历枚举
export const EducationLevel = {
  HIGH_SCHOOL: 1,
  TECHNICAL_SECONDARY: 2,
  JUNIOR_COLLEGE: 3,
  THIRD_BACHELOR: 4,
  SECOND_BACHELOR: 5,
  FIRST_BACHELOR: 6,
  MASTER: 7,
  DOCTOR: 8
};

// 性别枚举
export const Gender = {
  FEMALE: 0,
  MALE: 1
};

// 学校类型枚举
export const SchoolType = {
  ORDINARY: 1,
  DOUBLE_FIRST: 2,
  TOP_211_985: 3,
  TOP_4: 4
};

// 公司类型枚举
export const CompanyType = {
  SMALL_MICRO: 1,
  MEDIUM: 2,
  SECOND_TIER_INTERNET: 3,
  FIRST_TIER_INTERNET: 4,
  BBAT: 5
};

// 学历显示文本
export const getEducationText = (education: number): string => {
  const map: Record<number, string> = {
    [EducationLevel.HIGH_SCHOOL]: '高中',
    [EducationLevel.TECHNICAL_SECONDARY]: '中专',
    [EducationLevel.JUNIOR_COLLEGE]: '大专',
    [EducationLevel.THIRD_BACHELOR]: '三本学士',
    [EducationLevel.SECOND_BACHELOR]: '二本学士',
    [EducationLevel.FIRST_BACHELOR]: '一本学士',
    [EducationLevel.MASTER]: '硕士研究生',
    [EducationLevel.DOCTOR]: '博士生'
  };
  return map[education] || '未知';
};

// 性别显示文本
export const getGenderText = (gender: number): string => {
  return gender === Gender.MALE ? '男' : '女';
};

// 学校类型显示文本
export const getSchoolTypeText = (schoolType: number): string => {
  const map: Record<number, string> = {
    [SchoolType.ORDINARY]: '普通大学',
    [SchoolType.DOUBLE_FIRST]: '双一流',
    [SchoolType.TOP_211_985]: '211/985',
    [SchoolType.TOP_4]: '复北清交'
  };
  return map[schoolType] || '未知';
};

// 公司类型显示文本
export const getCompanyTypeText = (companyType: number): string => {
  const map: Record<number, string> = {
    [CompanyType.SMALL_MICRO]: '小微企业',
    [CompanyType.MEDIUM]: '中型企业',
    [CompanyType.SECOND_TIER_INTERNET]: '二线互联网企业',
    [CompanyType.FIRST_TIER_INTERNET]: '一线互联网',
    [CompanyType.BBAT]: 'BBAT'
  };
  return map[companyType] || '未知';
};

// 计算年龄
export const calculateAge = (birthDate: string): number => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// 计算工作年限
export const calculateWorkYears = (firstWorkDate: string): number => {
  const today = new Date();
  const firstWork = new Date(firstWorkDate);
  let years = today.getFullYear() - firstWork.getFullYear();
  const monthDiff = today.getMonth() - firstWork.getMonth();
  if (monthDiff < 0) {
    years--;
  }
  return Math.max(0, years);
}; 